import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils.js";

import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import LocationPinIcon from "../../assets/location-pin.svg?react";
import { AppContext, OverettContext, UserContext, VersionContext } from "../../Context";
import { changeRightPanelTab, setIsOpeningTool, setLeftPanelActiveTab } from "../../store/appSlice";
import { StoreState } from "../../store/rootReducer";
import { GetCodedValueAndAlias, getFeatureFromGlobalId, highlightFeature, Tool, zoomTo } from "../../utils/arcgisUtils";
import { onRefreshFeature } from "../../utils/helpers";
import { canEditVersion, isDefaultVersion } from "../../utils/versioningUtils";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import "./SearchItem.css";
import { LeftPanelTabs, RightPanelTabs } from "../../constants/Panels";
import { toast } from "react-toastify";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { AppDispatch } from "../../store/storeSetup";
import { OverettCreationStep } from "../MapTools/Overett/OverettEditorContent";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface SearchItemProps {
    feature: Graphic;
    storeHighlightHandle?: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures?: () => void;
    customSelectFunction?: (feature: Graphic, type: "show" | "edit") => void;
}

const SearchItem = (props: SearchItemProps) => {
    const { feature: initialFeature, storeHighlightHandle, clearHighlightedFeatures, customSelectFunction } = props;

    const [feature, setFeature] = useState<Graphic>(initialFeature);
    const dispatch = useDispatch<AppDispatch>();
    const appContext = useContext(AppContext);
    const userContext = useContext(UserContext);
    const overettContext = useContext(OverettContext);
    const setSelectedFeature = appContext!.selectedFeature.set;
    const setShownFeature = appContext!.shownFeature.set;
    const overettObjects = overettContext?.overettObjects.value;
    const versionContext = useContext(VersionContext);
    const seacableStartFeature = appContext?.seacableStartFeature;
    const seacableEndFeature = appContext?.seacableEndFeature;

    const versionServiceRef = versionContext?.versionServiceRef.value;

    const efsSelectedFeatures = appContext?.efsSelectedFeatures;
    const morildExportSelectedFeatures = appContext?.morildExportSelectedFeatures;

    const [symbolHtmlElement, setSymbolHtmlElement] = useState<HTMLElement | null>(null);
    const [canIEditVersion, setCanIEditVersion] = useState<boolean>(false);

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    const versionsSharedWithMe = useSelector((state: StoreState) => state.version.versionsSharedWithMe);
    const isCreatingOverett = useSelector((state: StoreState) => state.overett.isCreatingOverett);
    const isEditingOverett = useSelector((state: StoreState) => state.overett.isEditingOverett);
    const overettCreationStep = useSelector((state: StoreState) => state.overett.overettCreationStep);
    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);

    const getAttributeValue = (attributeName: string) => {
        return GetCodedValueAndAlias(feature.layer, attributeName, feature.attributes[attributeName])[1];
    };

    const mouseEnterHandler = async () => {
        if (!storeHighlightHandle || !feature) return;

        const highlightHandle = await highlightFeature(feature, "temporary");
        if (highlightHandle) {
            storeHighlightHandle(highlightHandle);
        }
    };

    const mouseLeaveHandler = async () => {
        if (!clearHighlightedFeatures) return;
        clearHighlightedFeatures();
    };

    const checkIsDefaultVersion = () => {
        if (!activeVersion || !versionServiceRef) return false;
        return isDefaultVersion(activeVersion, versionServiceRef?.current);
    };

    const isEfsFeatureSelected = () => {
        return efsSelectedFeatures?.value?.some(
            (efsFeature) => efsFeature.attributes.objectid === feature.attributes.objectid
        );
    };

    const isEfsFeature = () => {
        return Object.hasOwn(feature.attributes, "efs_melding");
    };

    const isMorildExportFeatureSelected = () => {
        return morildExportSelectedFeatures?.value?.some(
            (morildFeature) => morildFeature.attributes.objectid === feature.attributes.objectid
        );
    };

    const canExportToMorild = () => {
        const allowedLayers = [0, 1, 3, 5, 6];
        return allowedLayers.includes((feature.layer as FeatureLayer).layerId ?? -1);
    };

    const canBeSeacableStart = () => {
        if (feature.layer?.id === "Elektroskap") {
            return true;
        }
        return false;
    };

    const canBeSeacableEnd = () => {
        if (feature.layer?.id === "Elektroskap") {
            return true;
        }
        if (feature.layer?.id === "Lys") {
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (feature) {
            symbolUtils.getDisplayedSymbol(feature).then((symbol) => {
                if (symbol) {
                    symbolUtils.renderPreviewHTML(symbol, { size: 12 }).then((symbolElement) => {
                        if (!symbolElement) return;
                        setSymbolHtmlElement(symbolElement);
                    });
                }
            });
        }
    }, [feature]);

    useEffect(() => {
        if (activeVersion) {
            const user = userContext?.user.value;
            canEditVersion(activeVersion, versionsSharedWithMe ?? [], user).then((result) => {
                setCanIEditVersion(result);
            });
        }
    }, [activeVersion, versionsSharedWithMe]);

    // Update feature when prop changes
    useEffect(() => {
        setFeature(initialFeature);
    }, [initialFeature]);

    // Listen for refreshFeature events
    useEffect(() => {
        const removeListener = onRefreshFeature((layerId, objectId, shouldSearchItemRefresh) => {
            if (!shouldSearchItemRefresh) return;
            if (!feature) return;

            const layer = feature.layer as FeatureLayer;
            const currentLayerId = layer?.layerId?.toString() || layer?.id;
            const currentObjectId = feature.attributes?.objectid || feature.attributes?.OBJECTID;

            // Check if the event matches this search item's feature
            if (currentLayerId === layerId && currentObjectId === objectId) {
                const globalId = feature.attributes?.globalid || feature.attributes?.GlobalID;
                if (globalId && layer?.layerId !== undefined && layer?.layerId !== null) {
                    // Fetch the fresh feature using globalId
                    getFeatureFromGlobalId(layer.layerId, globalId).then((freshFeature) => {
                        if (freshFeature) {
                            setFeature(freshFeature);
                        }
                    });
                }
            }
        });

        return removeListener;
    }, [feature]);

    const canAddToUpperOverett = () => {
        if (overettObjects?.upper) return false;
        if (overettObjects?.upper?.attributes.objectid === feature.attributes.objectid) return false;
        if (overettObjects?.lower && overettObjects.lower.attributes.objectid === feature.attributes.objectid) {
            return false;
        }

        const hasRightLayer = ["Lys", "Fast sjømerke"].includes(feature.layer?.id.toString() ?? "");
        if (!hasRightLayer) return false;

        let isSameObjectType = true;

        if (overettObjects?.lower) {
            isSameObjectType = overettObjects.lower.layer?.id === feature.layer?.id;
        }

        return (
            (isCreatingOverett ||
                (isEditingOverett && (overettObjects?.upper === null || overettObjects?.lower === null))) &&
            isSameObjectType
        );
    };

    const canAddToLowerOverett = () => {
        if (overettObjects?.lower) return false;
        if (overettObjects?.lower?.attributes.objectid === feature.attributes.objectid) return false;
        if (overettObjects?.upper && overettObjects.upper.attributes.objectid === feature.attributes.objectid) {
            return false;
        }

        const hasRightLayer = ["Lys", "Fast sjømerke"].includes(feature.layer?.id.toString() ?? "");
        if (!hasRightLayer) return false;

        let isSameObjectType = true;

        if (overettObjects?.upper) {
            isSameObjectType = overettObjects.upper.layer?.id === feature.layer?.id;
        }

        return (
            (isCreatingOverett ||
                (isEditingOverett && (overettObjects?.lower === null || overettObjects?.upper === null))) &&
            isSameObjectType
        );
    };

    const setObjectAsShownObject = async () => {
        const layer = feature.layer as FeatureLayer;
        const globalId = feature.attributes?.globalid;
        let featureToUse = feature;
        if (layer.layerId && globalId) {
            const freshFeature = await getFeatureFromGlobalId(layer.layerId, globalId);
            if (freshFeature) {
                featureToUse = freshFeature;
            }
        }

        if (customSelectFunction) {
            customSelectFunction(featureToUse, "show");
        } else {
            setShownFeature(featureToUse);
        }
        dispatch(setLeftPanelActiveTab(LeftPanelTabs.VIEWER));
        if (feature.layer) {
            feature.layer.visible = true;
        }
    };

    const setObjectAsEditingObject = async () => {
        const result = dispatch(changeRightPanelTab(RightPanelTabs.EDITOR));
        if (!result) {
            // User cancelled, don't unselect
            return;
        }

        const layer = feature.layer as FeatureLayer;
        const globalId = feature.attributes?.globalid;
        let featureToUse = feature;
        if (layer.layerId && globalId) {
            const freshFeature = await getFeatureFromGlobalId(layer.layerId, globalId);
            if (freshFeature) {
                featureToUse = freshFeature;
            }
        }

        dispatch(setIsOpeningTool(Tool.FEATURE_EDITOR));
        appContext?.selectedFeatureHighlightRef.value.current?.remove();
        if (customSelectFunction) {
            customSelectFunction(featureToUse, "edit");
        } else {
            setSelectedFeature(featureToUse);
        }
        if (feature.layer) {
            feature.layer.visible = true;
        }
    };

    const navn = getAttributeValue("navn");
    const sted = getAttributeValue("sted");

    return (
        <div className={`search-item`} onMouseEnter={mouseEnterHandler} onMouseLeave={mouseLeaveHandler}>
            <div className="search-item-title">
                <div
                    className="search-item-symbol"
                    dangerouslySetInnerHTML={{ __html: symbolHtmlElement?.outerHTML ?? "" }}
                ></div>
                <span>{getAttributeValue("sjomerke_nr") ?? "Objid: " + feature.attributes.objectid}</span>
                <NfsButton onClick={() => zoomTo(feature, storeHighlightHandle, "temporary")} small empty>
                    <LocationPinIcon />
                </NfsButton>
            </div>
            <div className="search-item-info">
                {navn && (
                    <div>
                        <span>Navn</span>
                        <span className="search-item-info-value">{navn}</span>
                    </div>
                )}
                {sted && (
                    <div>
                        <span>Sted</span>
                        <span className="search-item-info-value">{sted}</span>
                    </div>
                )}
            </div>
            {getAttributeValue("nfs_status") && (
                <div className="search-item-status">
                    <span>
                        <strong>{getAttributeValue("nfs_status")}</strong>
                    </span>
                </div>
            )}
            <div className="flex-row small-gap w-100">
                <NfsButton outlined={!checkIsDefaultVersion()} onClick={setObjectAsShownObject}>
                    Vis
                </NfsButton>
                {canIEditVersion && <NfsButton onClick={setObjectAsEditingObject}>Redigér</NfsButton>}
            </div>
            {canAddToUpperOverett() && (
                <NfsButton
                    className="w-100"
                    onClick={() => {
                        overettContext?.overettObjects.set({ upper: feature, lower: overettObjects?.lower });
                    }}
                >
                    Bruk til ny øvre overett
                </NfsButton>
            )}
            {canAddToLowerOverett() && (
                <NfsButton
                    className="w-100"
                    onClick={() => {
                        overettContext?.overettObjects.set({ upper: overettObjects?.upper, lower: feature });
                    }}
                >
                    Bruk til ny nedre overett
                </NfsButton>
            )}
            {((isCreatingOverett && overettCreationStep === OverettCreationStep.SelectObjects) ||
                (isEditingOverett &&
                    (!overettObjects?.upper || !overettObjects?.lower) &&
                    overettCreationStep === OverettCreationStep.SelectObjects)) && (
                <>
                    {overettObjects?.upper?.attributes.objectid === feature.attributes.objectid && (
                        <NfsButton
                            className="w-100"
                            onClick={() => {
                                overettContext?.overettObjects.set({ upper: null, lower: overettObjects?.lower });
                            }}
                        >
                            Fjern fra øvre overett
                        </NfsButton>
                    )}
                    {overettObjects?.lower?.attributes.objectid === feature.attributes.objectid && (
                        <NfsButton
                            className="w-100"
                            onClick={() => {
                                overettContext?.overettObjects.set({ upper: overettObjects?.upper, lower: null });
                            }}
                        >
                            Fjern fra nedre overett
                        </NfsButton>
                    )}
                </>
            )}
            {checkIsDefaultVersion() &&
                rightPanelActiveTab === RightPanelTabs.EFS_MANAGER &&
                !isEfsFeatureSelected() &&
                isEfsFeature() && (
                    <NfsButton
                        outlined
                        className="w-100"
                        onClick={() => {
                            efsSelectedFeatures?.add(feature);
                        }}
                    >
                        Legg til i EFS
                    </NfsButton>
                )}
            {checkIsDefaultVersion() &&
                rightPanelActiveTab === RightPanelTabs.EFS_MANAGER &&
                isEfsFeatureSelected() && (
                    <NfsButton
                        outlined
                        className="w-100"
                        onClick={() => {
                            efsSelectedFeatures?.remove(feature);
                        }}
                    >
                        Fjern fra EFS
                    </NfsButton>
                )}
            {checkIsDefaultVersion() &&
                rightPanelActiveTab === RightPanelTabs.MORILD_EXPORT &&
                !isMorildExportFeatureSelected() &&
                canExportToMorild() && (
                    <NfsButton
                        outlined
                        className="w-100"
                        onClick={() => {
                            morildExportSelectedFeatures?.add(feature);
                        }}
                    >
                        Legg til i Morildeksport
                    </NfsButton>
                )}
            {checkIsDefaultVersion() &&
                rightPanelActiveTab === RightPanelTabs.MORILD_EXPORT &&
                isMorildExportFeatureSelected() && (
                    <NfsButton
                        outlined
                        className="w-100"
                        onClick={() => {
                            morildExportSelectedFeatures?.remove(feature);
                        }}
                    >
                        Fjern fra Morildeksport
                    </NfsButton>
                )}
            <div className="flex-row small-gap w-100">
                {!checkIsDefaultVersion() &&
                    rightPanelActiveTab === RightPanelTabs.SEA_CABLE &&
                    canBeSeacableStart() && (
                        <NfsButton
                            outlined
                            className="w-100"
                            onClick={() => {
                                if (feature.attributes.objectid === seacableEndFeature?.value?.attributes.objectid) {
                                    toast.warning("Startpunkt og sluttpunkt kan ikke være det samme");
                                    return;
                                }
                                if (feature.attributes.objectid === seacableStartFeature?.value?.attributes.objectid) {
                                    seacableStartFeature?.set(null);
                                } else {
                                    seacableStartFeature?.set(feature);
                                }
                            }}
                        >
                            {seacableStartFeature?.value?.attributes.objectid === feature.attributes.objectid
                                ? "Fjern startpunkt"
                                : "Startpunkt"}
                        </NfsButton>
                    )}
                {!checkIsDefaultVersion() && rightPanelActiveTab === RightPanelTabs.SEA_CABLE && canBeSeacableEnd() && (
                    <NfsButton
                        outlined
                        className="w-100"
                        onClick={async () => {
                            if (feature.attributes.objectid === seacableStartFeature?.value?.attributes.objectid) {
                                toast.warning("Startpunkt og sluttpunkt kan ikke være det samme");
                                return;
                            }
                            if (feature.attributes.objectid === seacableEndFeature?.value?.attributes.objectid) {
                                seacableEndFeature?.set(null);
                            } else {
                                seacableEndFeature?.set(feature);
                            }
                        }}
                    >
                        {seacableEndFeature?.value?.attributes.objectid === feature.attributes.objectid
                            ? "Fjern sluttpunkt"
                            : "Sluttpunkt"}
                    </NfsButton>
                )}
            </div>
        </div>
    );
};

export default SearchItem;
